import React, { useCallback, useState } from 'react'
import { Map } from 'immutable'
import { useSelector, useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { selectIsBreakpoint } from '../../services/app/selectors'
import { parse as parseQuery } from '../../services/query-string/index'
import { get as getConfig } from '../../config/index'
import Icon from '../Icon/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { setOraclePersona } from '../../services/oracle/actions'
import {
  AI_CONVERSATION_FAVORITED_GA4_EVENT,
  AI_CONVERSATION_NAME_UPDATED_GA4_EVENT,
  AI_CONVERSATION_DELETED_GA4_EVENT,
  AI_CONVERSATION_UNFAVORITED_GA4_EVENT,
  AI_CONVERSATION_STARTED_GA4_EVENT,
  AI_PERSONA_SELECTED_GA4_EVENT,
  UI_ELEMENT,
} from '../../services/event-tracking/index'
import { HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import { usePostHogEvent } from '../PostHog/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import { oracleHistoryRedirect } from './utils/oracleHistoryRedirect'
import { OracleSidebarItem } from './OracleSidebarItem'
import { OracleContextMenu, ContextMenuOption } from './OracleContextMenu'
import { OracleErrorToast } from '../OracleCommons/OracleErrorToast'
import { OracleSearchInput } from './OracleSearchInput'
import { OracleSearchResults } from './OracleSearchResults'
import { SidebarLogo } from './SidebarLogo'
import { SidebarNewConversationButtons } from './SidebarNewConversationButtons'
import { SidebarDivinationButtons, DivinationEntry } from './SidebarDivinationButtons'
import {
  buildNewConversationDestination,
  buildDivinationDestination,
} from './sidebarNavigationQuery'
import { getEnabledPersonalitiesWhereClause } from './personalityFilter'
import { makeIcon } from '../OracleCommons/icons'
import { Location, History } from './history-types'
import {
  GetHistoryQuery,
  GetHistoryDocument,
  useGetHistoryQuery,
  useDeleteChatSessionMutation,
  useOracleChatSessionFavoriteMutation,
  useOracleChatSessionRenameMutation,
  useSearchMessagePairsLazyQuery,
  useOraclePersonalityListQuery,
} from './queries.generated'
import { InfiniteScroll } from './InfiniteScroll'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { isGqlAborted } from './isGqlAborted'

interface OracleSidebarProps {
  onNewChat?: () => void
  staticText?: Map<string, string | Map<string, string>>
  location: Location
  history: History
  onItemSelect?: () => void
  onClose?: () => void
  currentPersonalityId?: string
}

type ChatSessionItem = GetHistoryQuery['oracleChatSessionList']['oracleChatSessions'][number]

export const OracleSidebar: React.FC<OracleSidebarProps> = ({
  onNewChat,
  staticText,
  location,
  history,
  onItemSelect,
  onClose,
  currentPersonalityId,
}) => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectPrimaryLanguage) || 'en'
  const dispatch = useDispatch()
  const isSmallBreakpoint = useSelector(selectIsBreakpoint(1024))
  const isLargeBreakpoint = useSelector(selectIsBreakpoint(1024))
  const config = getConfig()
  const multipleSearchResultsEnabled = config.features?.ai?.oracleMultipleSearchResults === 1

  // Get current chat session ID from URL
  const currentQuery = parseQuery(location.search)
  const currentChatSessionId = currentQuery.chatSessionId as string
  const selectedSnippetId = currentQuery.scrollTo as string | undefined
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [lastBatchSize, setLastBatchSize] = useState(0)
  const [contextMenu, setContextMenu] = useState<{
    isVisible: boolean
    chatSessionId: string | null
    position: { x: number; y: number } | null
    isFavorite: boolean
    triggerElement: HTMLElement | null
  }>({
    isVisible: false,
    chatSessionId: null,
    position: null,
    isFavorite: false,
    triggerElement: null,
  })

  // Error state for showing error toast
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  // Rename state management
  const [renameState, setRenameState] = useState<{
    isRenaming: boolean
    renamingSessionId: string | null
    newName: string
  }>({
    isRenaming: false,
    renamingSessionId: null,
    newName: '',
  })

  // Search state management
  const [searchQuery, setSearchQuery] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  // Note: "New Chat" session is now created only when user starts typing a message
  // This prevents it from appearing in the sidebar before any chat has started

  // Get translated text for context menu items
  const addToFavoritesLabel = useTranslatedText({ keys: ['contextMenu', 'addToFavorites'] })
  const removeFromFavoritesLabel = useTranslatedText({ keys: ['contextMenu', 'removeFromFavorites'] })
  const renameLabel = useTranslatedText({ keys: ['contextMenu', 'rename'] })
  const deleteLabel = useTranslatedText({ keys: ['contextMenu', 'delete'] })
  const renameFailedError = useTranslatedText({ keys: ['errors', 'renameFailed'] })
  const conversationLoadFailedError = useTranslatedText({ keys: ['errors', 'conversationLoadFailed'] })
  const favoritesLoadFailedError = useTranslatedText({ keys: ['errors', 'favoritesLoadFailed'] })
  const personalityLoadFailedError = useTranslatedText({ keys: ['errors', 'personalityLoadFailed'] })
  const deleteFailedError = useTranslatedText({ keys: ['errors', 'deleteFailed'] })
  const favoriteFailedError = useTranslatedText({ keys: ['errors', 'favoriteFailed'] })

  // Get translated text for sidebar items
  const conversationsLabel = useTranslatedText({ keys: ['sidebar', 'conversations'] })
  const favoritesLabel = useTranslatedText({ keys: ['sidebar', 'favorites'] })
  const closeSidebarLabel = useTranslatedText({ keys: ['sidebar', 'closeSidebar'] })
  const searchPlaceholder = useTranslatedText({ keys: ['sidebar', 'searchPlaceholder'] })

  // PostHog events
  const trackConversationDeleted = usePostHogEvent('ai_conversation_deleted')
  const trackConversationNameUpdated = usePostHogEvent('ai_conversation_name_updated')
  const trackConversationFavorited = usePostHogEvent('ai_conversation_favorited')
  const trackConversationUnfavorited = usePostHogEvent('ai_conversation_unfavorited')

  // --------- Fetch favorites chat sessions
  const {
    data: favoritesChatSessionsData,
    error: favoritesChatSessionsError,
    loading: favoritesChatSessionsLoading,
  } = useGetHistoryQuery({
    variables: {
      limit: 100,
      offset: 0,
      favorite: true,
      order: [['activityUpdatedAt', 'DESC']], // Most recent first
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })
  // --------- Fetch non-favorites chat sessions (the default state for conversations)

  const {
    data: nonFavoritesChatSessionsData,
    error: nonFavoritesChatSessionsError,
    loading: nonFavoritesChatSessionsLoading,
    fetchMore: fetchMoreChatSessions,
  } = useGetHistoryQuery({
    variables: {
      limit: 100,
      offset: 0,
      favorite: false,
      order: [['activityUpdatedAt', 'DESC']], // Most recent first
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  // Get personality data for mapping using where clause to filter enabled personalities
  const { data: personalityData, error: personalityError } = useOraclePersonalityListQuery({
    variables: {
      limit: 5,
      offset: 0,
      order: [['id', 'ASC']],
      where: getEnabledPersonalitiesWhereClause(),
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-first',
  })

  // Search GraphQL hook
  const [searchMessagePairs,
    { data: searchData, loading: searchLoading }] = useSearchMessagePairsLazyQuery()

  const apolloClient = useApolloClient()

  // Create personality mapping - personalities are already filtered by the where clause
  const personalityMap = React.useMemo(() => {
    if (!personalityData?.oraclePersonalityList?.oraclePersonalities) {
      return {} as Record<string, string>
    }
    // No need to filter since the where clause already filtered for enabled personalities
    return personalityData.oraclePersonalityList.oraclePersonalities.reduce((acc, p) => {
      acc[p.id] = p.key.toLowerCase()
      return acc
    }, {} as Record<string, string>)
  }, [personalityData])

  const [deleteChatSession] = useDeleteChatSessionMutation()
  const [setFavorite] = useOracleChatSessionFavoriteMutation()
  const [renameChatSession] = useOracleChatSessionRenameMutation()

  // Handle non-favorites query error with toast
  React.useEffect(() => {
    if (nonFavoritesChatSessionsError && !nonFavoritesChatSessionsData) {
      setErrorMessage(conversationLoadFailedError)
      setShowErrorToast(true)
    }
  }, [nonFavoritesChatSessionsError, nonFavoritesChatSessionsData, conversationLoadFailedError])

  // Handle favorites query error with toast
  React.useEffect(() => {
    if (favoritesChatSessionsError && !favoritesChatSessionsData) {
      setErrorMessage(favoritesLoadFailedError)
      setShowErrorToast(true)
    }
  }, [favoritesChatSessionsError, favoritesChatSessionsData, favoritesLoadFailedError])

  // Handle personality list query error with toast
  React.useEffect(() => {
    if (personalityError && !personalityData) {
      setErrorMessage(personalityLoadFailedError)
      setShowErrorToast(true)
    }
  }, [personalityError, personalityData, personalityLoadFailedError])

  // When the user has scrolled down the list of sessions and we're in danger of
  // showing the bottom of the list, this is invoked to load additional data.
  const handleLoadMoreChatSessions = useCallback(async () => {
    if (!nonFavoritesChatSessionsData || isLoadingMore) return

    const currentOffset = nonFavoritesChatSessionsData.oracleChatSessionList.offset
    const currentLimit = nonFavoritesChatSessionsData.oracleChatSessionList.limit
    const nextOffset = currentOffset + currentLimit
    setIsLoadingMore(true)
    try {
      await fetchMoreChatSessions({
        variables: {
          limit: currentLimit,
          offset: nextOffset,
          order: [['activityUpdatedAt', 'DESC']], // Ensure consistent ordering
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const newItems = fetchMoreResult.oracleChatSessionList.oracleChatSessions
          setLastBatchSize(newItems.length)

          return {
            oracleChatSessionList: {
              ...fetchMoreResult.oracleChatSessionList,
              oracleChatSessions: [
                ...prev.oracleChatSessionList.oracleChatSessions,
                ...newItems,
              ],
            },
          } satisfies GetHistoryQuery
        },
      })
    } finally {
      setIsLoadingMore(false)
    }
  }, [nonFavoritesChatSessionsData, fetchMoreChatSessions, isLoadingMore])

  const handleConversationSelect = useCallback(() => {
    if (onItemSelect) {
      onItemSelect()
    }
  }, [onItemSelect])

  // Handle search result conversation selection - the Link component handles navigation,
  // this just ensures side effects are called
  const handleSearchConversationSelect = useCallback((chatSessionId: string) => {
    if (!chatSessionId) return
    // The OracleSidebarItem's Link component handles navigation and clears search params
    // We just need to call onItemSelect for side effects
    if (onItemSelect) {
      onItemSelect()
    }
  }, [onItemSelect])

  // Handle search snippet selection: set temporal URL params for deep-linking
  const handleSearchResultSelect = useCallback((
    chatSessionId: string,
    scrollTo?: string,
    pos?: number,
    snippet?: string,
    matchSource?: string,
  ) => {
    if (!chatSessionId) return

    const currentQueryForNav = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const queryWithoutPersonality = ((obj: Record<string, string>) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { oraclePersonalityId, ...rest } = obj
      return rest
    })(currentQueryForNav as Record<string, string>)

    const newQuery: Record<string, string> = {
      ...queryWithoutPersonality,
      chatSessionId,
    }

    if (scrollTo && typeof pos === 'number') {
      newQuery.scrollTo = scrollTo
      newQuery.pos = String(pos)
      newQuery.searchActive = 'true' // Mark that we're viewing a search result
      if (snippet) {
        newQuery.snippet = encodeURIComponent(snippet)
      }
      if (matchSource) {
        newQuery.matchSource = matchSource
      }
    }

    oracleHistoryRedirect({
      history,
      url: URL_AI_CHAT,
      query: newQuery,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
    })

    if (onItemSelect) {
      onItemSelect()
    }
  }, [location.search, history, auth, language, onItemSelect])

  // Search handlers
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)

    // Clear search results when query is cleared
    if (value.length === 0) {
      // Clear Apollo cache for search results
      apolloClient.cache.evict({
        fieldName: 'searchMessagePairs',
      })
      apolloClient.cache.gc()
      setHasSearched(false)
    }
  }, [apolloClient])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    // Clear Apollo cache for search results
    apolloClient.cache.evict({
      fieldName: 'searchMessagePairs',
    })
    apolloClient.cache.gc()
    setHasSearched(false)

    // Remove searchActive from URL to trigger highlight cleanup
    const currentQueryForNav = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { searchActive, ...queryWithoutSearch } = currentQueryForNav as Record<string, string>

    oracleHistoryRedirect({
      history,
      url: location.pathname,
      query: queryWithoutSearch,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
    })
  }, [apolloClient, location.search, location.pathname, history, auth])

  // Handler for the two new-conversation buttons (Sage / Architect). Mirrors
  // the OraclePersonalitySelector.handleNewChat flow: clears chatSessionId,
  // sets the personality on the URL, dispatches GA4 events, and calls
  // onItemSelect so mobile dismisses the sidebar.
  const handleNewConversation = useCallback((personalityId: string) => {
    const { url, query } = buildNewConversationDestination(
      currentQuery as Record<string, unknown>,
      URL_AI_CHAT,
      personalityId,
    )

    oracleHistoryRedirect({
      history,
      url,
      query,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
    })

    dispatch(setOraclePersona(personalityId))
    dispatch(setAiGa4Event(AI_PERSONA_SELECTED_GA4_EVENT))
    dispatch(setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.NEW_BUTTON,
    })))

    if (onItemSelect) {
      onItemSelect()
    }
  }, [
    currentQuery, history, location.pathname, auth, language, dispatch, onItemSelect,
  ])

  // Handler for the divination buttons (Tarot / Oracle / Horoscope).
  // Navigates to /aichat/conversation/<entry> (AI-821) and dismisses sidebar.
  // Uses a history push (the default) — not replace — so that the tarot
  // category buttons' subsequent `history.goBack()` returns to the previous
  // page rather than overshooting past it (AI-818 bug #3).
  const handleDivinationSelect = useCallback((entry: DivinationEntry) => {
    const { url, query } = buildDivinationDestination(
      currentQuery as Record<string, unknown>,
      URL_AI_CHAT,
      entry,
    )

    oracleHistoryRedirect({
      history,
      url,
      query,
      auth,
      language,
    })

    if (onItemSelect) {
      onItemSelect()
    }
  }, [currentQuery, history, location.pathname, auth, language, onItemSelect])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      searchMessagePairs({
        variables: {
          query: searchQuery.trim(),
          limit: 50,
          offset: 0,
        },
        context: {
          jwt: auth.get('jwt'),
        },
      })
      setHasSearched(true)
    }
  }, [searchQuery, searchMessagePairs, auth])

  const handleContextMenu = useCallback((
    chatSessionId: string,
    triggerElement: HTMLElement,
    isFavorite: boolean,
  ) => {
    // Calculate position coordinates from the trigger element
    const rect = triggerElement.getBoundingClientRect()
    const position = {
      x: rect.right + 8, // Start 8px to the right of trigger
      y: rect.top,
    }
    setContextMenu({
      isVisible: !contextMenu.isVisible || contextMenu.chatSessionId !== chatSessionId,
      chatSessionId,
      position,
      isFavorite,
      triggerElement,
    })
  }, [contextMenu.isVisible, contextMenu.chatSessionId])

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({
      isVisible: false,
      chatSessionId: null,
      position: null,
      isFavorite: false,
      triggerElement: null,
    })
  }, [])

  const handleDeleteChatSession = useCallback(async (chatSessionId: string) => {
    if (!chatSessionId) return

    const wasCurrentSession = currentChatSessionId === chatSessionId

    try {
      await deleteChatSession({
        variables: { chatSessionId },
        context: {
          jwt: auth.get('jwt'),
          useMaster: true,
        },
        refetchQueries: ['GetHistory'],
      })
    } catch (error) {
      const isAborted = isGqlAborted(error)
      if (!isAborted) {
        setErrorMessage(deleteFailedError)
        setShowErrorToast(true)
      }
      return
    }

    // Close context menu
    handleCloseContextMenu()

    // If this was the current chat session, remove chatSessionId from URL
    if (wasCurrentSession) {
      // Use onNewChat to remove chatSessionId from URL and redirect to new chat
      if (onNewChat) {
        onNewChat() // This will handle the URL update by removing chatSessionId
      }
    }

    // AI GA4 event
    dispatch(setAiGa4Event(AI_CONVERSATION_DELETED_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.CONVERSATION_LIST,
    })))
    trackConversationDeleted()
  }, [deleteChatSession, auth, location.search, onNewChat, dispatch, handleCloseContextMenu,
    trackConversationDeleted])

  // Rename handlers
  const handleStartRename = useCallback((sessionId: string, currentName: string) => {
    setRenameState({
      isRenaming: true,
      renamingSessionId: sessionId,
      newName: currentName,
    })
    handleCloseContextMenu()
  }, [handleCloseContextMenu])

  const handleCancelRename = useCallback(() => {
    setRenameState({
      isRenaming: false,
      renamingSessionId: null,
      newName: '',
    })
  }, [])

  const handleSaveRename = useCallback(async () => {
    if (!renameState.renamingSessionId || !renameState.newName.trim()) {
      handleCancelRename()
      return
    }

    const trimmedName = renameState.newName.trim()

    try {
      await renameChatSession({
        variables: {
          input: {
            id: renameState.renamingSessionId,
            name: trimmedName,
          },
        },
        context: {
          jwt: auth.get('jwt'),
          useMaster: true,
        },
        update: (cache, { data }) => {
          if (!data?.oracleChatSessionRename?.success) return

          // AI GA4 event
          dispatch(setAiGa4Event(AI_CONVERSATION_NAME_UPDATED_GA4_EVENT.mergeDeep({
            ui_element: UI_ELEMENT.CONVERSATION_LIST,
          })))
          trackConversationNameUpdated()

          // Read both queries from cache
          const favoritesQuery = cache.readQuery<GetHistoryQuery>({
            query: GetHistoryDocument,
            variables: { limit: 10, offset: 0, favorite: true },
          })
          const nonFavoritesQuery = cache.readQuery<GetHistoryQuery>({
            query: GetHistoryDocument,
            variables: { limit: 10, offset: 0, favorite: false },
          })

          // Find the item in either cache
          let targetItem = null
          if (favoritesQuery?.oracleChatSessionList?.oracleChatSessions) {
            targetItem = favoritesQuery.oracleChatSessionList.oracleChatSessions
              .find((item) => item.id === renameState.renamingSessionId)
          }
          if (!targetItem && nonFavoritesQuery?.oracleChatSessionList?.oracleChatSessions) {
            targetItem = nonFavoritesQuery.oracleChatSessionList.oracleChatSessions
              .find((item) => item.id === renameState.renamingSessionId)
          }

          if (!targetItem) return

          // Create updated item with new name
          const updatedItem = {
            ...targetItem,
            name: trimmedName,
            updatedAt: new Date().toISOString(),
          }

          // Update both caches with the new name
          if (targetItem.favorite && favoritesQuery) {
            cache.writeQuery<GetHistoryQuery>({
              query: GetHistoryDocument,
              variables: { limit: 10, offset: 0, favorite: true },
              data: {
                oracleChatSessionList: {
                  ...favoritesQuery.oracleChatSessionList,
                  oracleChatSessions: favoritesQuery.oracleChatSessionList.oracleChatSessions
                    .map((item) => (
                      item.id === renameState.renamingSessionId ? updatedItem : item
                    )),
                },
              },
            })
          }
          if (!targetItem.favorite && nonFavoritesQuery) {
            cache.writeQuery<GetHistoryQuery>({
              query: GetHistoryDocument,
              variables: { limit: 10, offset: 0, favorite: false },
              data: {
                oracleChatSessionList: {
                  ...nonFavoritesQuery.oracleChatSessionList,
                  oracleChatSessions: nonFavoritesQuery.oracleChatSessionList.oracleChatSessions
                    .map((item) => (
                      item.id === renameState.renamingSessionId ? updatedItem : item
                    )),
                },
              },
            })
          }
        },
      })

      handleCancelRename()
    } catch (error) {
      const isAborted = isGqlAborted(error)

      if (!isAborted) {
        setErrorMessage(renameFailedError)
        setShowErrorToast(true)
      }

      handleCancelRename()
    }
  }, [renameState, renameChatSession, auth, handleCancelRename])

  const getContextMenuOptions = useCallback((): ContextMenuOption[] => [
    {
      id: contextMenu.isFavorite ? 'remove-from-favorites' : 'add-to-favorites',
      label: contextMenu.isFavorite ? removeFromFavoritesLabel : addToFavoritesLabel,
      iconName: contextMenu.isFavorite ? 'heart-off' : 'heart',
      onClick: async () => {
        const newFavoriteStatus = !contextMenu.isFavorite
        try {
          await setFavorite({
            context: {
              jwt: auth.get('jwt'),
              useMaster: true,
            },
            variables: {
              chatSessionId: contextMenu.chatSessionId,
              favorite: newFavoriteStatus,
            },
            optimisticResponse: {
              oracleChatSessionFavorite: {
                __typename: 'OracleChatSessionFavoriteResponse',
                success: true,
                errors: null,
                oracleChatSession: {
                  __typename: 'OracleChatSession',
                  name: '', // Will be filled by cache
                  favorite: newFavoriteStatus,
                  favoriteUpdatedAt: new Date().toISOString(),
                },
              },
            },
            update: (cache, { data }) => {
              if (!data?.oracleChatSessionFavorite?.success) return

              // Read both queries from cache
              const favoritesQuery = cache.readQuery<GetHistoryQuery>({
                query: GetHistoryDocument,
                variables: { limit: 10, offset: 0, favorite: true },
              })
              const nonFavoritesQuery = cache.readQuery<GetHistoryQuery>({
                query: GetHistoryDocument,
                variables: { limit: 10, offset: 0, favorite: false },
              })

              // Find the item in either cache
              let targetItem = null

              if (favoritesQuery?.oracleChatSessionList?.oracleChatSessions) {
                targetItem = favoritesQuery.oracleChatSessionList.oracleChatSessions
                  .find((item) => item.id === contextMenu.chatSessionId)
              }

              if (!targetItem && nonFavoritesQuery?.oracleChatSessionList?.oracleChatSessions) {
                targetItem = nonFavoritesQuery.oracleChatSessionList.oracleChatSessions
                  .find((item) => item.id === contextMenu.chatSessionId)
              }

              if (!targetItem) return

              // Create updated item with new favorite status
              const updatedItem = {
                ...targetItem,
                favorite: newFavoriteStatus,
                favoriteUpdatedAt: new Date().toISOString(),
              }

              if (newFavoriteStatus) {
              // Moving to favorites: remove from non-favorites, add to favorites
                if (nonFavoritesQuery) {
                  cache.writeQuery<GetHistoryQuery>({
                    query: GetHistoryDocument,
                    variables: { limit: 10, offset: 0, favorite: false },
                    data: {
                      oracleChatSessionList: {
                        ...nonFavoritesQuery.oracleChatSessionList,
                        oracleChatSessions:
                          nonFavoritesQuery.oracleChatSessionList.oracleChatSessions
                            .filter((item) => item.id !== contextMenu.chatSessionId),
                      },
                    },
                  })
                }
                if (favoritesQuery) {
                  cache.writeQuery<GetHistoryQuery>({
                    query: GetHistoryDocument,
                    variables: { limit: 10, offset: 0, favorite: true },
                    data: {
                      oracleChatSessionList: {
                        ...favoritesQuery.oracleChatSessionList,
                        oracleChatSessions: [
                          updatedItem,
                          ...favoritesQuery.oracleChatSessionList.oracleChatSessions,
                        ],
                      },
                    },
                  })
                }
              } else {
              // Moving to non-favorites: remove from favorites, add to non-favorites
                if (favoritesQuery) {
                  cache.writeQuery<GetHistoryQuery>({
                    query: GetHistoryDocument,
                    variables: { limit: 10, offset: 0, favorite: true },
                    data: {
                      oracleChatSessionList: {
                        ...favoritesQuery.oracleChatSessionList,
                        oracleChatSessions: favoritesQuery.oracleChatSessionList.oracleChatSessions
                          .filter((item) => item.id !== contextMenu.chatSessionId),
                      },
                    },
                  })
                }
                if (nonFavoritesQuery) {
                  cache.writeQuery<GetHistoryQuery>({
                    query: GetHistoryDocument,
                    variables: { limit: 10, offset: 0, favorite: false },
                    data: {
                      oracleChatSessionList: {
                        ...nonFavoritesQuery.oracleChatSessionList,
                        oracleChatSessions: [
                          updatedItem,
                          ...nonFavoritesQuery.oracleChatSessionList.oracleChatSessions,
                        ],
                      },
                    },
                  })
                }
              }
            },
            refetchQueries: ['GetHistory'],
          })
          // AI GA4 event
          const event = newFavoriteStatus
            ? AI_CONVERSATION_FAVORITED_GA4_EVENT
            : AI_CONVERSATION_UNFAVORITED_GA4_EVENT

          dispatch(setAiGa4Event(event.mergeDeep({
            ui_element: UI_ELEMENT.CONVERSATION_LIST,
          })))
          if (newFavoriteStatus) {
            trackConversationFavorited()
          } else {
            trackConversationUnfavorited()
          }
          handleCloseContextMenu()
        } catch (error) {
          const isAborted = isGqlAborted(error)
          if (!isAborted) {
            setErrorMessage(favoriteFailedError)
            setShowErrorToast(true)
          }
        }
      },
    },
    {
      id: 'rename',
      label: renameLabel,
      iconName: 'pencil',
      onClick: () => {
        if (contextMenu.chatSessionId) {
          // Find the current name from the session data
          const allSessions = [
            ...(favoritesChatSessionsData?.oracleChatSessionList?.oracleChatSessions || []),
            ...(nonFavoritesChatSessionsData?.oracleChatSessionList?.oracleChatSessions || []),
          ]
          const currentSession = allSessions.find(
            (session) => session.id === contextMenu.chatSessionId,
          )
          const currentName = currentSession?.name || ''

          handleStartRename(contextMenu.chatSessionId, currentName)
        }
      },
    },
    {
      id: 'delete',
      label: deleteLabel,
      iconName: 'trash',
      variant: 'destructive' as const,
      onClick: () => {
        if (contextMenu.chatSessionId) {
          handleDeleteChatSession(contextMenu.chatSessionId)
        }
      },
    },
  ], [
    addToFavoritesLabel, removeFromFavoritesLabel, renameLabel, deleteLabel,
    contextMenu.chatSessionId, contextMenu.isFavorite,
    handleDeleteChatSession, dispatch, handleCloseContextMenu, setFavorite, auth,
    handleStartRename, favoritesChatSessionsData, nonFavoritesChatSessionsData,
  ])

  // Early returns after all hooks for non-favorites
  if (nonFavoritesChatSessionsLoading && !nonFavoritesChatSessionsData) {
    return null
  }
  if (!nonFavoritesChatSessionsData && !nonFavoritesChatSessionsError) {
    return null
  }

  // Early returns after all hooks for favorites
  if (favoritesChatSessionsLoading && !favoritesChatSessionsData) {
    return null
  }
  if (!favoritesChatSessionsData && !favoritesChatSessionsError) {
    return null
  }

  const faveHistoryItems = favoritesChatSessionsData
    ?.oracleChatSessionList?.oracleChatSessions || []

  // Create favorite conversation IDs set for search results
  const favoriteConversationIds = new Set(
    faveHistoryItems.map((item) => item.id),
  )

  // Handle error case with fallback data
  const nonFaveHistoryItems = nonFavoritesChatSessionsData
    ?.oracleChatSessionList?.oracleChatSessions || []
  const limit = nonFavoritesChatSessionsData?.oracleChatSessionList?.limit || 100
  const currentCount = nonFaveHistoryItems.length

  // Set initial lastBatchSize if we haven't loaded more yet
  if (lastBatchSize === 0 && currentCount > 0) {
    setLastBatchSize(currentCount)
  }

  return (
    <div className="oracle-sidebar">
      <div className="oracle-sidebar__header">
        <SidebarLogo
          width={isLargeBreakpoint ? 159 : 239}
          height={isLargeBreakpoint ? 32 : 48}
        />
        {isSmallBreakpoint && onClose && (
          <button
            className="oracle-sidebar__close-button"
            onClick={onClose}
            type="button"
            aria-label={closeSidebarLabel}
          >
            <Icon color="currentColor" size={24}>
              {makeIcon('close')}
            </Icon>
          </button>
        )}
      </div>

      {/* New conversation buttons (Sage / Architect) — replace the old
          OraclePersonalitySelector toggle. Hidden when searching. */}
      {!hasSearched && (
        <SidebarNewConversationButtons
          onNewChat={handleNewConversation}
          selectedPersonalityId={currentPersonalityId}
        />
      )}

      {/* Divider between the "new conversation" buttons and what comes next.
          Hidden when searching, since the only thing shown then is results. */}
      {!hasSearched && (
        <div className="oracle-sidebar__divider" role="separator" />
      )}

      {/* Divination buttons (Tarot / Oracle / Horoscope) — gated by PostHog
          per-button feature flags. Hidden when searching. */}
      {!hasSearched && (
        <SidebarDivinationButtons onSelect={handleDivinationSelect} />
      )}

      {/* Search Input */}
      <OracleSearchInput
        value={searchQuery}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        onClear={handleClearSearch}
        placeholder={searchPlaceholder}
      />

      {/* Search Results - shown when hasSearched */}
      {hasSearched ? (
        <OracleSearchResults
          searchData={searchData}
          loading={searchLoading}
          onItemSelect={handleSearchConversationSelect}
          onSnippetSelect={(
            chatSessionId,
            messagePairId,
            messageIndexInConversation,
            snippet,
            matchSource,
          ) => handleSearchResultSelect(
            chatSessionId,
            messagePairId,
            messageIndexInConversation,
            snippet,
            matchSource,
          )}
          location={location}
          staticText={staticText}
          selectedSnippetId={selectedSnippetId}
          personalityMap={personalityMap}
          currentChatSessionId={currentChatSessionId}
          favoriteConversationIds={favoriteConversationIds}
          onContextMenu={handleContextMenu}
          contextMenu={contextMenu}
          renameState={renameState}
          onStartRename={handleStartRename}
          onCancelRename={handleCancelRename}
          onSaveRename={handleSaveRename}
          multipleSearchResultsEnabled={multipleSearchResultsEnabled}
        />
      ) : (
        /* Original conversation lists */
        <div className="oracle-sidebar__content">
        {faveHistoryItems && faveHistoryItems.length > 0 && (
          <SidebarSection
            historyItems={faveHistoryItems}
            staticText={staticText}
            location={location}
            onItemSelect={handleConversationSelect}
            onContextMenu={handleContextMenu}
            conversationsLabel={favoritesLabel}
            personalityMap={personalityMap}
            currentChatSessionId={currentChatSessionId}
            contextMenu={contextMenu}
            renameState={renameState}
            onStartRename={handleStartRename}
            onCancelRename={handleCancelRename}
            onSaveRename={handleSaveRename}
          />)

        }
        {nonFaveHistoryItems && (
          <SidebarSection
            historyItems={nonFaveHistoryItems}
            staticText={staticText}
            location={location}
            onItemSelect={handleConversationSelect}
            onContextMenu={handleContextMenu}
            conversationsLabel={conversationsLabel}
            personalityMap={personalityMap}
            currentChatSessionId={currentChatSessionId}
            contextMenu={contextMenu}
            renameState={renameState}
            onStartRename={handleStartRename}
            onCancelRename={handleCancelRename}
            onSaveRename={handleSaveRename}
          />
        )}
        {/* We can use isLoadingMore to conditionally show a loading skelly right here */}
        <InfiniteScroll
          onLoadMore={handleLoadMoreChatSessions}
          currentCount={currentCount}
          lastBatchSize={lastBatchSize}
          limit={limit}
          loading={isLoadingMore}
        />
      </div>
      )}
      <OracleContextMenu
        isVisible={contextMenu.isVisible}
        onClose={handleCloseContextMenu}
        options={contextMenu.chatSessionId ? getContextMenuOptions() : []}
        position={contextMenu.position}
        triggerElement={contextMenu.triggerElement}
      />

      <OracleErrorToast
        isVisible={showErrorToast}
        onClose={() => {
          setShowErrorToast(false)
          setErrorMessage(undefined)
        }}
        message={errorMessage}
      />
    </div>
  )
}

const SidebarSection = ({
  historyItems,
  staticText,
  location,
  onItemSelect,
  onContextMenu,
  conversationsLabel,
  personalityMap,
  currentChatSessionId,
  contextMenu,
  renameState,
  onStartRename,
  onCancelRename,
  onSaveRename,
}: {
  historyItems: Array<ChatSessionItem>
  staticText?: Map<string, string | Map<string, string>>
  location: Location
  onItemSelect?: () => void
  onContextMenu?: (chatSessionId: string, triggerElement: HTMLElement, isFavorite: boolean) => void
  conversationsLabel: string
  personalityMap: Record<string, string>
  currentChatSessionId?: string
  contextMenu: {
    isVisible: boolean
    chatSessionId: string | null
    position: { x: number; y: number } | null
  }
  renameState: {
    isRenaming: boolean
    renamingSessionId: string | null
    newName: string
  }
  onStartRename: (sessionId: string, currentName: string) => void
  onCancelRename: () => void
  onSaveRename: () => void
}) => {
  return (
    <div className="oracle-sidebar__section">
      <div className="oracle-sidebar__section-header">
        <span className="oracle-sidebar__section-title">
          {conversationsLabel}
        </span>
      </div>
      <div className="oracle-sidebar__section-items">
        {historyItems.map((historyItem) => (
          <OracleSidebarItem
            key={historyItem.id}
            chatSessionId={historyItem.id}
            title={historyItem.name}
            personalityId={historyItem.personalityId}
            personalityKey={personalityMap[historyItem.personalityId]}
            isSelected={historyItem.id === currentChatSessionId}
            isContextMenuActive={
              contextMenu.isVisible && contextMenu.chatSessionId === historyItem.id
            }
            onSelect={() => {
              if (onItemSelect) {
                onItemSelect()
              }
            }}
            onContextMenu={(triggerElement) => {
              if (onContextMenu) {
                onContextMenu(historyItem.id, triggerElement, historyItem.favorite)
              }
            }}
            staticText={staticText}
            location={location}
            renameState={renameState}
            onStartRename={onStartRename}
            onCancelRename={onCancelRename}
            onSaveRename={onSaveRename}
          />
        ))}
      </div>
    </div>
  )
}
